import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFavorites } from '@/hooks/useFavorites';
import { useTourDiscounts, resolveTourDiscount, applyTourDiscount } from '@/hooks/useTourDiscounts';
import SelectChevron from '@/components/base/SelectChevron';

interface Tour {
  id: string;
  title: string;
  duration: string;
  spots: number;
  category: string;
  group_type: string;
  price: number;
  image: string;
}

interface TourPackagesProps {
  tours: Tour[];
  loading?: boolean;
}

export default function TourPackages({ tours, loading }: TourPackagesProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { discounts } = useTourDiscounts();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [durationFilter, setDurationFilter] = useState('All');

  return (
    <section id="tours" className="w-full bg-background-200 py-12 md:py-16 lg:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary-900">
            {t('tourPackages.title')}
          </h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={durationFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setDurationFilter(value);
                  if (value !== 'All') {
                    navigate(`/tours?duration=${encodeURIComponent(value)}#tour-packages`);
                  }
                }}
                className="appearance-none bg-white border border-secondary-200 rounded-lg px-4 py-2 pr-8 text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              >
                <option value="All">{t('tourPackages.durationAll')}</option>
                <option value="1-3 Days">{t('tourPackages.days1to3')}</option>
                <option value="4-7 Days">{t('tourPackages.days4to7')}</option>
                <option value="8+ Days">{t('tourPackages.days8plus')}</option>
              </select>
              <SelectChevron className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400" />
            </div>
            <Link
              to="/tours#tour-packages"
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('tourPackages.viewAll')}
            </Link>
          </div>
        </div>

        {/* Loading / Empty states */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-secondary-100 overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-secondary-100" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-secondary-100 rounded w-2/3" />
                  <div className="h-3 bg-secondary-100 rounded w-1/2" />
                  <div className="h-5 bg-secondary-100 rounded w-3/4" />
                  <div className="flex justify-between items-center">
                    <div className="h-6 bg-secondary-100 rounded w-20" />
                    <div className="h-8 bg-secondary-100 rounded-full w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : tours.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-map-pin-line text-4xl text-secondary-300 mb-3" />
            <p className="text-secondary-500 text-sm">{t('tourPackages.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {tours.slice(0, 12).map((tour) => {
              const priceInfo = applyTourDiscount(
                tour.price,
                resolveTourDiscount(tour.id, discounts),
              );
              return (
                <Link
                  key={tour.id}
                  to={`/tour/${tour.id}`}
                  className="group block bg-white rounded-xl border border-secondary-100 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-primary-400 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={tour.image}
                      alt={tour.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(tour.id);
                      }}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                    >
                      <i
                        className={`${
                          isFavorite(tour.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-secondary-500'
                        } text-sm`}
                      />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Meta tags */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs text-secondary-500">
                        <i className="ri-time-line" />
                        {tour.duration}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-secondary-500">
                        <i className="ri-map-pin-line" />
                        {t('tourPackages.spots', { count: tour.spots })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {(tour.category || '').split(',').map((c) => c.trim()).filter(Boolean).map((cat) => (
                        <span key={cat} className="px-2 py-0.5 bg-secondary-100 text-secondary-600 text-xs rounded-md">
                          {cat}
                        </span>
                      ))}
                      <span className="px-2 py-0.5 bg-secondary-100 text-secondary-600 text-xs rounded-md">
                        {tour.group_type}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-medium text-secondary-900 text-sm md:text-base mb-3 line-clamp-2 min-h-[2.5rem]">
                      {tour.title}
                    </h3>

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-secondary-500">{t('tourPackages.from')}</span>
                        <div className="flex items-baseline gap-1.5">
                          {priceInfo.hasDiscount && (
                            <span className="text-sm font-medium text-secondary-400 line-through">
                              {priceInfo.originalPrice} USD
                            </span>
                          )}
                          <p className="text-lg font-bold text-secondary-900">
                            {priceInfo.finalPrice} USD
                          </p>
                        </div>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 px-4 py-2 border border-primary-500 text-primary-600 text-sm font-medium rounded-full group-hover:bg-primary-50 transition-colors whitespace-nowrap"
                      >
                        {t('tourPackages.view')}
                        <i className="ri-arrow-right-line text-xs" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}